import {
  ExperienceMapper,
  ExperienceEntryLike,
  AudienceMapper,
  AudienceEntryLike,
} from '@ninetailed/experience.js-utils-contentful';
import { createClient } from 'contentful';
import { contentfulConfig } from '../libs/contentful/contentful';
import { Sys } from '../libs/contentful/props';
import { getNinetailedMode } from './serverExperiences';
import { ExtendedExperienceConfiguration } from 'libs/contentful/queries/ninetailed';
import { Reference } from '@ninetailed/experience.js-shared';

export const getContentfulClient = (options?: { draftPreviewEnabled?: boolean }) => {
  const isDraftPreviewEnabled = options?.draftPreviewEnabled ?? false;

  return createClient({
    space: contentfulConfig.spaceId,
    accessToken: isDraftPreviewEnabled ? contentfulConfig.previewAccessToken : contentfulConfig.accessToken,
    environment: contentfulConfig.environment,
    host: isDraftPreviewEnabled ? 'preview.contentful.com' : undefined,
  });
};

/**
 * Recursively removes circular references from an object
 * @param entry - The entry to clean
 * @param seen - Set of entries already seen
 * @returns A clean version of the object without circular references
 */
function removeCircularReferences(entry: unknown, seen = new WeakSet<object>()): unknown {
  if (entry === null || typeof entry !== 'object') {
    return entry;
  }

  if (seen.has(entry)) {
    const contentfulEntry = entry as { sys: Sys };
    return contentfulEntry?.sys ? { sys: { id: contentfulEntry.sys.id } } : {};
  }

  seen.add(entry);

  if (Array.isArray(entry)) {
    return entry.map(item => removeCircularReferences(item, seen));
  }

  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(entry as Record<string, unknown>)) {
    cleaned[key] = removeCircularReferences(value, seen);
  }

  seen.delete(entry);
  return cleaned;
}

export const getAllExperiences = async (options?: { draftPreviewEnabled?: boolean }) => {
  const client = getContentfulClient(options);
  const ninetailedMode = await getNinetailedMode();

  const entries = await client.getEntries({
    content_type: 'nt_experience',
    include: 1,
  });

  // Clean circular references when in client mode to avoid errors
  const processedEntries =
    ninetailedMode === 'client' ? entries.items.map(entry => removeCircularReferences(entry)) : entries.items;

  return (processedEntries as unknown as ExperienceEntryLike[])
    .filter(ExperienceMapper.isExperienceEntry)
    .map(ExperienceMapper.mapExperience);
};

export const getAllAudiences = async (options?: { draftPreviewEnabled?: boolean }) => {
  const client = getContentfulClient(options);

  const entries = await client.getEntries({
    content_type: 'nt_audience',
  });

  return (entries.items as unknown as AudienceEntryLike[])
    .filter(AudienceMapper.isAudienceEntry)
    .map(AudienceMapper.mapAudience)
    .map(audience => ({
      ...audience,
      name: audience.name || audience.id,
    }));
};

/**
 * Filters out null values from an array of experiences
 * @param experiences - The array of experiences to filter
 * @returns The filtered array of experiences
 */
export function filterValidExperiences<T extends Reference>(
  experiences: Array<ExtendedExperienceConfiguration<T> | null>,
): ExtendedExperienceConfiguration<T>[] {
  return experiences.filter((exp): exp is ExtendedExperienceConfiguration<T> => exp !== null);
}
